import type { TFunction } from 'i18next';
import { ApiError } from '@/api/client';
import { NETWORK_ERROR_STATUS, NO_AUTH_TOKEN_STATUS, RecognitionApiError } from '@/api/recognition/client';

/**
 * Traduce un fallo de guardado a algo que se pueda **accionar**.
 *
 * 🔴 Nació de un fallo real: al guardar el catálogo de dominios la pantalla decía *«No se pudo
 * guardar. Revisa que la configuración sea válida»* — y mandó a revisar el validador durante un buen
 * rato, cuando la causa era que el `PUT` se iba al servicio equivocado. Ese mensaje se mostraba ante
 * **cualquier** error, así que un 404, un 403 de permisos o una sesión caducada se leían todos como
 * «tu configuración está mal». Es peor que no decir nada: dirige la investigación al sitio
 * equivocado.
 *
 * Este endpoint es `[Authorize(Roles = "AdminGlobal")]`, y en este producto el 403 no es hipotético:
 * hay antecedente de administradores que perdieron el rol al vaciarse la semilla.
 *
 * 🔴 Recibe `t` en vez de devolver texto fijo: la primera versión traía los seis mensajes escritos
 * en español dentro del módulo, o sea que en las otras seis lenguas la pantalla contestaba en
 * español. Un mensaje de error es la parte de la interfaz que MÁS se lee con prisa.
 */
export function describirFalloAlGuardar(error: unknown, t: TFunction): string {
  const fallo = comoFalloHttp(error);
  if (!fallo) return t('jobs.sync.errNetwork');

  // Sin respuesta HTTP real: `fetch` que nunca contestó (-1) o URL del servicio sin configurar (0).
  // Un status sintético NO es un código del servidor, así que no debe salir como «error -1».
  if (fallo.status === NETWORK_ERROR_STATUS || fallo.status === 0) return t('jobs.sync.errNetwork');
  // Aún no hay token (la petición ni siquiera salió): es un asunto de sesión, no de red.
  if (fallo.status === NO_AUTH_TOKEN_STATUS) return t('jobs.sync.errSession');

  switch (fallo.status) {
    case 401:
      return t('jobs.sync.errSession');
    case 403:
      return t('jobs.sync.errForbidden');
    case 400:
    case 422: {
      const detalle = detalleDelServidor(fallo.body);
      return detalle
        ? t('jobs.sync.errValidation', { detalle })
        : t('jobs.sync.errValidationPlain');
    }
    default:
      return t('jobs.sync.errOther', { status: fallo.status });
  }
}

/**
 * El servidor contestó (o el cliente lo simula) con un estado y un cuerpo.
 *
 * 🔴 Son DOS clases de error, no una: `ApiError` (cliente de `api/client`) y `RecognitionApiError`
 * (cliente de `api/recognition`, que es el que usa el guardado de `sync-settings`). Ninguna hereda de
 * la otra. Reconocer solo `ApiError` hacía que cualquier fallo real del guardado —un 400 de validación, un
 * 403, un 401— se leyera como «no hubo respuesta del servidor», que manda a revisar la red cuando el
 * servidor sí contestó.
 */
function comoFalloHttp(error: unknown): { status: number; body: unknown } | null {
  if (error instanceof ApiError || error instanceof RecognitionApiError) {
    return { status: error.status, body: error.body };
  }
  return null;
}

/**
 * Saca el motivo del cuerpo de la respuesta.
 *
 * El servicio devuelve un ProblemDetails con `title`, y las validaciones de FluentValidation llegan en
 * `errors` como un diccionario de campo → mensajes. Se prefieren los `errors` porque nombran **el
 * campo concreto**, que es justo lo que faltaba: el validador rechaza cosas que esta pantalla no
 * muestra (`MaxAbsentPercent`, `OverrideFields`), y sin el nombre no hay forma de saberlo desde aquí.
 *
 * El detalle NO se traduce: lo redacta el servidor. Traducirlo exigiría un catálogo paralelo de sus
 * mensajes, que se desincronizaría en el primer cambio del validador.
 */
function detalleDelServidor(body: unknown): string | null {
  if (typeof body !== 'object' || body === null) return null;

  const cuerpo = body as { title?: unknown; detail?: unknown; errors?: unknown };

  if (cuerpo.errors && typeof cuerpo.errors === 'object') {
    const mensajes = Object.entries(cuerpo.errors as Record<string, unknown>)
      .map(([campo, valor]) => `${campo}: ${Array.isArray(valor) ? valor.join(' ') : String(valor)}`);
    if (mensajes.length > 0) return mensajes.join(' · ');
  }

  if (typeof cuerpo.detail === 'string' && cuerpo.detail.trim() !== '') return cuerpo.detail;
  if (typeof cuerpo.title === 'string' && cuerpo.title.trim() !== '') return cuerpo.title;

  return null;
}
